import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { isQuizRuntimeHref } from '../quiz/quizRuntimeNavigation';
import { useOptionalQuizRuntimeLaunch } from '../quiz/QuizRuntimeLaunchProvider';
import { BookOpen, ClipboardCheck, FileText, Mic, PenLine, UserRound } from 'lucide-react';
import { Badge, BrandLogo, Button, Progress, buttonVariants, cn } from '@examflow/ui';
import { buildPlatformScope } from '@examflow/core';
import type {
  ExamWorkspaceSkillRail,
  LearnerSectionContentReadiness,
  MetricModel,
  PageAction,
  TrainingLoopStep,
  UiTone,
  UserLearningContext,
} from '@examflow/learning-space';
import { buildScopedLearningHref } from '@examflow/learning-space';
import {
  formatExamSpaceIdentityLabel,
  resolveExamSpaceTheme,
} from '@examflow/learning-space';
import { usePlatformSettings } from '../platform/PlatformSettingsContext';
import type { IncompleteTrainingCardModel } from './useIncompleteTrainingCard';

/** Primary closed-loop nav — retention tools stay in page footer only. */
type ShellNavId = 'workspace' | 'training' | 'mock-exam' | 'diagnosis';

interface LearningShellProps {
  viewer: UserLearningContext;
  activeNav: ShellNavId;
  statusLabel?: string;
  children: ReactNode;
}

const navItems: Array<{
  id: ShellNavId;
  label: string;
  href: string;
}> = [
  { id: 'workspace', label: '总览', href: '/workspace' },
  { id: 'training', label: '专项训练', href: '/training' },
  { id: 'mock-exam', label: '模拟考试', href: '/mock-exam' },
  { id: 'diagnosis', label: '能力诊断', href: '/diagnosis/ability' },
];

function ShellNavLinks({
  activeNav,
  activeScope,
  accent,
  accentSoft,
  orientation,
  hiddenNavIds,
}: {
  activeNav: ShellNavId;
  activeScope: ReturnType<typeof buildPlatformScope>;
  accent: string;
  accentSoft: string;
  orientation: 'horizontal' | 'vertical';
  hiddenNavIds?: ReadonlySet<ShellNavId>;
}) {
  return (
    <nav
      className={
        orientation === 'horizontal'
          ? 'flex items-center gap-1 overflow-x-auto text-sm'
          : 'flex flex-col gap-1 text-sm'
      }
      aria-label="考试空间导航"
      data-testid={
        orientation === 'vertical' ? 'exam-space-shell-nav-side' : 'exam-space-shell-nav-mobile'
      }
    >
      {navItems
        .filter((item) => !hiddenNavIds?.has(item.id))
        .map((item) => {
        const active = item.id === activeNav;
        const href = buildScopedLearningHref(item.href, activeScope);
        return (
          <Link
            key={item.id}
            to={href}
            className={cn(
              'font-medium transition-colors',
              orientation === 'horizontal'
                ? 'shrink-0 rounded-full px-3 py-1.5'
                : 'rounded-md px-3 py-2',
              active ? 'text-[#111827]' : 'text-slate-500 hover:text-slate-800'
            )}
            style={active ? { backgroundColor: accentSoft, color: accent } : undefined}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

const toneToChipClass: Record<UiTone, string> = {
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
  current: 'bg-[#e7eeff] text-[#1f4fd8] border-[#c8d6ff]',
  success: 'bg-[#e5f4f2] text-[#0f8b8d] border-[#b9dcd8]',
  warning: 'bg-[#fff2d8] text-[#b86b16] border-[#f0d293]',
  danger: 'bg-[#fdecea] text-[#c94a3a] border-[#efbbb4]',
  locked: 'bg-slate-100 text-slate-500 border-slate-200',
};

const actionVariantByKind: Record<PageAction['kind'], 'primary' | 'outline' | 'danger' | 'ghost'> =
  {
    primary: 'primary',
    secondary: 'outline',
    danger: 'danger',
    ghost: 'ghost',
  };

export function LearningShell({
  viewer,
  activeNav,
  statusLabel = 'ready',
  children,
}: LearningShellProps) {
  const { settings, learnerGates } = usePlatformSettings();
  const activeScope = buildPlatformScope({ examType: viewer.activeExamSpace.examCode });
  const theme = resolveExamSpaceTheme(viewer.activeExamSpace.examCode);
  const identityLabel = formatExamSpaceIdentityLabel(
    viewer.activeExamSpace.examCode,
    viewer.activeExamSpace.examName
  );
  const hiddenNavIds = new Set<ShellNavId>();
  if (!learnerGates.mockExam) {
    hiddenNavIds.add('mock-exam');
  }
  const brandName = settings.siteName?.trim() || 'ExamFlow';

  return (
    <div
      className="min-h-screen text-[#111827]"
      style={{ backgroundColor: theme.surface }}
      data-exam-space={theme.examCode}
      data-testid="exam-space-shell"
    >
      <header
        className="border-b bg-white/90 backdrop-blur-sm"
        style={{ borderColor: theme.border }}
        data-testid="exam-space-env-bar"
      >
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-6 px-6 sm:px-8">
          <Link to="/" className="inline-flex items-center" aria-label={`${brandName} 首页`}>
            <BrandLogo variant="full" size={28} alt={brandName} />
          </Link>

          <div
            className="hidden items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold sm:flex"
            style={{
              borderColor: theme.border,
              backgroundColor: theme.accentSoft,
              color: theme.accent,
            }}
          >
            <span>{identityLabel}</span>
            <span className="text-[10px] font-medium opacity-70">·</span>
            <span className="font-medium opacity-80">{statusLabel}</span>
          </div>

          <div className="flex items-center gap-4 text-sm text-slate-600">
            <Link
              to="/profile"
              aria-label="个人中心"
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-medium text-[#111827] transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              style={{
                borderColor: theme.border,
                backgroundColor: theme.accentSoft,
                outlineColor: theme.accent,
              }}
            >
              <UserRound className="h-4 w-4" />
              {viewer.userName}
            </Link>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-6 pb-3 md:hidden sm:px-8">
          <ShellNavLinks
            activeNav={activeNav}
            activeScope={activeScope}
            accent={theme.accent}
            accentSoft={theme.accentSoft}
            orientation="horizontal"
            hiddenNavIds={hiddenNavIds}
          />
        </div>
      </header>
      <div className="mx-auto flex max-w-6xl gap-8 px-6 py-8 sm:px-8 sm:py-10">
        <aside className="hidden w-44 shrink-0 md:block" aria-label="考试空间侧栏">
          <ShellNavLinks
            activeNav={activeNav}
            activeScope={activeScope}
            accent={theme.accent}
            accentSoft={theme.accentSoft}
            orientation="vertical"
            hiddenNavIds={hiddenNavIds}
          />
        </aside>
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}

export function PageIntro({
  eyebrow,
  title,
  subtitle,
  primaryAction,
  secondaryAction,
  onPrimaryAction,
  accent,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  primaryAction?: PageAction;
  secondaryAction?: PageAction;
  onPrimaryAction?: () => void;
  accent?: string;
}) {
  return (
    <div className="mb-7 flex items-start justify-between gap-8">
      <div>
        <div
          className="mb-2 text-xs font-semibold"
          style={{ color: accent ?? '#1f4fd8' }}
        >
          {eyebrow}
        </div>
        <h1 className="max-w-3xl text-[32px] font-bold leading-tight tracking-normal text-[#111827]">
          {title}
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">{subtitle}</p>
      </div>
      <div className="flex shrink-0 items-center gap-3 pt-6">
        {secondaryAction && <ActionLink action={secondaryAction} />}
        {primaryAction && <ActionLink action={primaryAction} onClick={onPrimaryAction} />}
      </div>
    </div>
  );
}

export function ActionLink({
  action,
  className,
  onClick,
}: {
  action: PageAction;
  className?: string;
  onClick?: () => void;
}) {
  const { launchQuizRuntime } = useOptionalQuizRuntimeLaunch();
  const variant = actionVariantByKind[action.kind];

  if (!action.href || action.disabled) {
    return (
      <span className="inline-flex max-w-full flex-col gap-1">
        <button
          type="button"
          disabled={action.disabled}
          onClick={action.disabled ? undefined : onClick}
          title={action.disabledReason}
          className={cn(buttonVariants({ variant }), 'min-w-36 rounded-[8px]', className)}
        >
          {action.label}
        </button>
        {action.disabled && action.disabledReason ? (
          <span className="max-w-sm text-xs leading-5 text-slate-500">{action.disabledReason}</span>
        ) : null}
      </span>
    );
  }

  if (isQuizRuntimeHref(action.href)) {
    const href = action.href;
    return (
      <button
        type="button"
        className={cn(buttonVariants({ variant }), 'min-w-36 rounded-[8px]', className)}
        data-testid="quiz-runtime-new-tab"
        onClick={() => {
          onClick?.();
          void launchQuizRuntime(href);
        }}
      >
        {action.label}
      </button>
    );
  }

  return (
    <Link
      to={action.href}
      className={cn(buttonVariants({ variant }), 'min-w-36 rounded-[8px]', className)}
    >
      {action.label}
    </Link>
  );
}

export function MetricStrip({ metrics }: { metrics: MetricModel[] }) {
  return (
    <div className="grid grid-cols-4 gap-4">
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className={cn('rounded-[8px] border bg-white p-4', toneToChipClass[metric.tone])}
        >
          <div className="text-3xl font-bold leading-none tracking-normal">{metric.value}</div>
          <div className="mt-3 text-xs font-medium text-slate-500">{metric.label}</div>
          {metric.caption && <div className="mt-1 text-xs text-slate-400">{metric.caption}</div>}
        </div>
      ))}
    </div>
  );
}

export function Panel({
  title,
  subtitle,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-[8px] border border-[#dbe4d8] bg-white p-6', className)}>
      <div className="mb-5">
        <h2 className="text-lg font-bold tracking-normal text-[#111827]">{title}</h2>
        {subtitle && <p className="mt-2 text-sm leading-6 text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

export function RetentionToolsPanel({
  tools,
}: {
  tools: Array<{
    id: string;
    title: string;
    description: string;
    action: PageAction;
  }>;
}) {
  return (
    <Panel
      title="更多工具"
      subtitle="留存入口，不改变工作台「下一步」训练处方。"
      className="mt-7"
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {tools.map((tool) => (
          <div
            key={tool.id}
            className="flex flex-col justify-between rounded-[8px] border border-[#e7eee4] bg-[#f8faf6] p-4"
          >
            <div>
              <div className="text-sm font-bold text-[#111827]">{tool.title}</div>
              <p className="mt-2 text-xs leading-5 text-slate-500">{tool.description}</p>
            </div>
            <div className="mt-4">
              <ActionLink action={tool.action} className="min-w-0 w-full" />
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function ToneBadge({ tone, children }: { tone: UiTone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex rounded-full border px-3 py-1 text-xs font-semibold',
        toneToChipClass[tone]
      )}
    >
      {children}
    </span>
  );
}

export function RecentTrainingRecordsPanel({
  records,
  status = 'ready',
  title = '最近训练记录',
  subtitle,
  emptyMessage = '完成训练后，这里会显示最近记录。',
  className,
  variant = 'choice',
  evidenceNote,
  incomplete = null,
  onContinueIncomplete,
  onDiscardIncomplete,
}: {
  records: RecentTrainingRecordItem[];
  status?: 'loading' | 'ready' | 'unavailable';
  title?: string;
  subtitle?: string;
  emptyMessage?: string;
  className?: string;
  variant?: 'choice' | 'writing';
  /** Writing evidence focus line (same source as Ability writingFocus). */
  evidenceNote?: string | null;
  /** Local incomplete Session (choice Training only) — not a server Attempt. */
  incomplete?: IncompleteTrainingCardModel | null;
  onContinueIncomplete?: () => void;
  onDiscardIncomplete?: () => void;
}) {
  const hasRecords = records.length > 0;
  const showIncomplete =
    Boolean(incomplete) &&
    typeof onContinueIncomplete === 'function' &&
    typeof onDiscardIncomplete === 'function';

  return (
    <section className={cn('rounded-[8px] border border-[#dbe4d8] bg-white p-6', className)}>
      <div className="mb-5">
        <h2 className="text-lg font-bold tracking-normal text-[#111827]">{title}</h2>
        {subtitle && <p className="mt-2 text-sm leading-6 text-slate-500">{subtitle}</p>}
        {evidenceNote ? (
          <p
            className="mt-3 rounded-[8px] border border-[#dbe4d8] bg-[#f8faf6] px-3 py-2 text-sm leading-6 text-slate-700"
            data-testid="writing-evidence-focus-note"
          >
            {evidenceNote}
          </p>
        ) : null}
      </div>

      {showIncomplete && incomplete ? (
        <article
          className="mb-3 rounded-[8px] border border-[#f0d293] bg-[#fff8e8] px-4 py-4"
          data-testid="incomplete-training-card"
        >
          <div className="text-sm font-bold text-[#111827]">未完成的训练</div>
          <div className="mt-1 text-sm font-semibold text-primary-700">{incomplete.scopeLabel}</div>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            共 {incomplete.totalQuestions} 题，已答 {incomplete.answeredCount}{' '}
            题。继续本轮会恢复本地进度；开新一轮会丢弃本轮未交卷作答。
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2.5">
            <Button type="button" size="md" variant="outline" onClick={onDiscardIncomplete}>
              开新一轮
            </Button>
            <Button type="button" size="md" onClick={onContinueIncomplete}>
              继续本轮
            </Button>
          </div>
        </article>
      ) : null}

      {status === 'loading' ? (
        <div className="rounded-[8px] border border-[#dbe4d8] bg-[#f8faf6] px-4 py-5 text-sm font-semibold text-slate-500">
          正在读取本地训练记录
        </div>
      ) : status === 'unavailable' ? (
        <div className="rounded-[8px] border border-[#f0d293] bg-[#fff8e8] px-4 py-5 text-sm font-semibold text-[#b86b16]">
          本地训练记录暂时不可用
        </div>
      ) : hasRecords ? (
        <div className="space-y-3">
          {records.map((record) => {
            if (variant === 'writing') {
              const writing = summarizeWritingRecordScore(record);
              return (
                <article
                  key={record.id}
                  className="rounded-[8px] border border-[#dbe4d8] bg-[#f8faf6] px-4 py-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-bold text-[#111827]">
                        {record.moduleLabel}
                        {writing.taskLabel ? ` · ${writing.taskLabel}` : ''}
                      </div>
                      <div className="mt-2 text-xs font-semibold text-slate-500">
                        {formatTrainingAttemptDuration(record.durationSeconds)}
                        {writing.scoreKind === 'heuristic' ? ' · 参考分' : ''}
                        {writing.weakestLabel ? ` · 弱项 ${writing.weakestLabel}` : ''}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold leading-none text-[#1f4fd8]">
                        {writing.scoreLabel}
                      </div>
                      <div className="mt-2 text-xs font-semibold text-slate-500">
                        {writing.scoreKind === 'none'
                          ? '待评分'
                          : writing.scoreKind === 'heuristic'
                            ? '不计主证据'
                            : 'AI 评分'}
                      </div>
                    </div>
                  </div>
                </article>
              );
            }

            return (
              <article
                key={record.id}
                className="rounded-[8px] border border-[#dbe4d8] bg-[#f8faf6] px-4 py-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-bold text-[#111827]">
                      {record.moduleLabel}
                      {(record.answeredCount ?? 0) > 0
                        ? ` · ${record.answeredCount}/${record.totalQuestions} 题`
                        : record.intensityLabel
                          ? ` · ${record.intensityLabel}`
                          : ''}
                    </div>
                    <div className="mt-2 text-xs font-semibold text-slate-500">
                      {record.totalQuestions} 题 ·{' '}
                      {formatTrainingAttemptDuration(record.durationSeconds)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold leading-none text-[#1f4fd8]">
                      {record.accuracy}%
                    </div>
                    <div className="mt-2 text-xs font-semibold text-slate-500">
                      {record.correctCount} / {record.totalQuestions}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : showIncomplete ? null : (
        <div className="rounded-[8px] border border-[#dbe4d8] bg-[#f8faf6] px-4 py-5 text-sm font-semibold text-slate-500">
          {emptyMessage}
        </div>
      )}
    </section>
  );
}

function summarizeWritingRecordScore(record: RecentTrainingRecordItem): {
  scoreLabel: string;
  scoreKind: 'llm' | 'heuristic' | 'none';
  taskLabel: string | null;
  weakestLabel: string | null;
} {
  const metrics = record.questionMetrics ?? [];
  const openEnded =
    metrics.find((m) => m.responseMode === 'open_ended' && typeof m.aiScore === 'number') ??
    metrics.find((m) => typeof m.aiScore === 'number') ??
    metrics.find((m) => m.responseMode === 'open_ended') ??
    metrics[0];
  const taskLabel =
    openEnded?.taskNumber != null ? `任务 ${openEnded.taskNumber}` : null;
  const dims = openEnded?.aiDimensions ?? [];
  const weakest =
    dims.length > 0
      ? [...dims].sort((a, b) => a.score - b.score || a.id.localeCompare(b.id))[0]
      : null;
  if (openEnded?.aiScore == null) {
    return { scoreLabel: '—', scoreKind: 'none', taskLabel, weakestLabel: null };
  }
  const max = openEnded.aiScoreMax ?? 20;
  const kind = openEnded.aiGradeSource === 'heuristic' ? 'heuristic' : 'llm';
  return {
    scoreLabel: `${Math.round(openEnded.aiScore * 10) / 10}/${max}`,
    scoreKind: kind,
    taskLabel,
    weakestLabel: weakest?.label ?? null,
  };
}

export function SectionContentReadinessList({
  sections,
}: {
  sections: LearnerSectionContentReadiness[];
}) {
  if (sections.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {sections.map((section) => {
        const ready = section.status === 'ready';

        return (
          <div
            key={section.sectionId}
            className="grid grid-cols-[1fr_88px_76px] items-center rounded-[8px] border border-[#dbe4d8] bg-[#f8faf6] px-4 py-3 text-sm"
          >
            <span className="font-semibold text-[#111827]">{section.label}</span>
            <span className="font-mono text-slate-600">{section.learnableBankCount} 个题库</span>
            <Badge tone={ready ? 'success' : 'warning'} size="sm" className="justify-center">
              {ready ? '已覆盖' : '待补齐'}
            </Badge>
          </div>
        );
      })}
    </div>
  );
}

export function SkillRailsBand({
  rails,
  accent,
  highlightedModuleId,
  title = '考试结构',
  subtitle = '按 Skill 进入专项训练',
  structureShape,
}: {
  rails: ExamWorkspaceSkillRail[];
  accent: string;
  highlightedModuleId?: string | null;
  title?: string;
  subtitle?: string;
  structureShape?: string;
}) {
  if (rails.length === 0) return null;

  return (
    <section
      className="mt-10"
      data-testid="exam-space-skill-rails"
      data-structure-shape={structureShape}
    >
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <h2 className="text-[11px] font-bold tracking-[0.18em] text-slate-500">{title}</h2>
        <p className="text-[11px] text-slate-500">{subtitle}</p>
      </div>
      <div
        className={cn(
          'grid gap-3',
          rails.length >= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1 sm:grid-cols-3'
        )}
      >
        {rails.map((rail) => {
          const highlighted = highlightedModuleId === rail.moduleId;
          const contentReady = rail.status === 'ready';
          // Default true when older package builds omit the field.
          const runtimeLaunchable = rail.runtimeLaunchable !== false;
          const ready = contentReady && runtimeLaunchable;
          const blockedByRuntime = contentReady && !runtimeLaunchable;
          const cardClassName = cn(
            'group relative min-h-[120px] overflow-hidden rounded-md border bg-white/70 p-4',
            ready ? 'transition-shadow hover:shadow-md' : 'opacity-90',
            highlighted ? 'border-transparent' : 'border-white/60'
          );
          const cardStyle = highlighted
            ? {
                boxShadow: `inset 3px 0 0 ${accent}, 0 1px 2px rgba(28,42,34,0.06)`,
                background: `linear-gradient(145deg, ${accent}12 0%, rgba(255,255,255,0.85) 55%)`,
              }
            : undefined;
          const statusLabel = ready ? '可练' : blockedByRuntime ? '即将开放' : '待补齐';
          const ctaLabel = ready
            ? '进入训练 →'
            : blockedByRuntime
              ? '开放作答暂未接入'
              : '内容待补齐';
          const body = (
            <>
              <div className="flex items-start justify-between gap-2">
                <ModuleIcon moduleId={rail.moduleId} />
                <span
                  className={cn(
                    'text-[10px] font-bold',
                    ready ? 'text-slate-600' : 'text-slate-400'
                  )}
                >
                  {statusLabel}
                </span>
              </div>
              <div className="mt-3 text-2xl font-black tracking-tight text-[#111827]">
                {rail.shortLabel}
              </div>
              <div className="mt-1 text-xs text-slate-500">{rail.label}</div>
              <div
                className={cn(
                  'mt-4 text-[11px] font-semibold tracking-wide',
                  ready && 'transition-transform group-hover:translate-x-0.5'
                )}
                style={{ color: ready ? accent : '#94a3b8' }}
              >
                {ctaLabel}
              </div>
            </>
          );

          if (!ready) {
            return (
              <div
                key={rail.moduleId}
                className={cardClassName}
                style={cardStyle}
                aria-disabled="true"
                title={
                  rail.runtimeBlockReason ??
                  (blockedByRuntime ? '该 Skill 开放作答尚未接入' : '该 Skill 暂无可学内容')
                }
              >
                {body}
              </div>
            );
          }

          return (
            <Link key={rail.moduleId} to={rail.href} className={cardClassName} style={cardStyle}>
              {body}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export interface RecentTrainingRecordItem {
  id: string;
  moduleLabel: string;
  intensityLabel: string;
  totalQuestions: number;
  answeredCount?: number;
  correctCount: number;
  accuracy: number;
  durationSeconds: number;
  questionMetrics?: Array<{
    responseMode?: 'closed' | 'open_ended';
    aiScore?: number;
    aiScoreMax?: number;
    aiGradeSource?: 'llm' | 'heuristic';
    taskNumber?: number;
    aiDimensions?: Array<{
      id: string;
      label: string;
      score: number;
      action?: string;
    }>;
  }>;
}

export function LoopRail({
  steps,
  accent = '#1f4fd8',
}: {
  steps: TrainingLoopStep[];
  accent?: string;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-5 gap-y-2"
      data-testid="exam-space-loop-rail"
    >
      {steps.map((step) => (
        <div key={step.id} className="flex items-center gap-2 text-sm">
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              step.status === 'done' && 'bg-[#0f8b8d]',
              step.status !== 'current' && step.status !== 'done' && 'bg-[#cad8c7]'
            )}
            style={step.status === 'current' ? { backgroundColor: accent } : undefined}
          />
          <span
            className={cn(
              step.status === 'current' ? 'font-bold text-[#111827]' : 'text-slate-500'
            )}
          >
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function formatTrainingAttemptDuration(durationSeconds: number): string {
  const displaySeconds = Math.max(1, durationSeconds);

  if (displaySeconds < 60) {
    return `${displaySeconds} 秒`;
  }

  return `${Math.round(displaySeconds / 60)} 分钟`;
}

export function DimensionProgress({
  label,
  value,
  max,
  tone,
  note,
}: {
  label: string;
  value: number;
  max: number;
  tone: UiTone;
  note: string;
}) {
  return (
    <div className="grid grid-cols-[150px_1fr_72px] items-center gap-4">
      <div className="text-sm font-semibold text-[#111827]">{label}</div>
      <Progress value={value} max={max} tone={tone} valueLabel={`${value}/${max}`} />
      <Badge tone={tone} size="sm" className="justify-center">
        {note}
      </Badge>
    </div>
  );
}

export function ModuleIcon({ moduleId }: { moduleId: string }) {
  if (moduleId === 'listening') return <ClipboardCheck className="h-5 w-5" />;
  if (moduleId === 'reading') return <FileText className="h-5 w-5" />;
  if (moduleId === 'writing') return <PenLine className="h-5 w-5" />;
  if (moduleId === 'speaking') return <Mic className="h-5 w-5" />;
  return <BookOpen className="h-5 w-5" />;
}
